/**
 * ProjectOfficialArtifactEditView — `/projects/$projectId/official/$artifactId/edit`
 *
 * Editor del borrador de un artifact. Solo se monta si la versión que se
 * edita es editable según el servidor (`version.is_editable`, OT-0064) y el
 * usuario puede editar el artifact (`canEditArtifact`). Si no, muestra el
 * motivo y un link al viewer.
 *
 * Patrón:
 *  - Carga el snapshot del proyecto (template definition con i18n) +
 *    el artifact + la version actual (mismo trío que el viewer).
 *  - Construye state local `content: Record<section_id, value>` desde
 *    `version.content`. Cada section_id se controla por
 *    `<SectionEditor>`.
 *  - Bottom bar fijo con "Cancelar" + "Guardar borrador". Al guardar:
 *    PUT /artifacts/{id}/content → toast → invalida queries.
 *
 * Submit-for-review entra en B.6.c.2.
 */

import { useMemo, useState } from 'react'
import {
  Link,
  useNavigate,
  useParams,
  useSearch,
} from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  Eye,
  FileText,
  GitBranch,
  Layers,
  MessageSquare,
  Sparkles,
} from 'lucide-react'

import { ApiError } from '../../api/client'
import {
  getArtifact,
  getArtifactVersion,
  submitForReview,
  updateArtifactContent,
} from '../../api/artifacts'
import { useMe } from '../../auth/useMe'
import {
  fetchSnapshotTemplate,
  type SnapshotTemplateItem,
} from '../../api/projects'
import { listProjectMembers } from '../../api/project_members'
import {
  invalidateVersionAiMetadata,
  pendingObservations,
  useVersionCitations,
  useVersionGroundings,
} from '../../api/citations'
import { useAccessToken } from '../../auth/useAccessToken'
import { useApiErrorMessage } from '../../hooks/useApiErrorMessage'
import { useProjectAccess } from '../../hooks/useProjectAccess'
import { canEditArtifact, canWriteInTenant, isExternal } from '../../lib/tiers'
import { isVersionLockError, markVersionLocked } from '../../lib/versionLock'
import { GenerateAllDialog } from '../../components/ai/GenerateAllDialog'
import { ProposeStructureDialog } from '../../components/ai/ProposeStructureDialog'
import { EditorContextBanner } from '../../components/editor/EditorContextBanner'
import { CommentsPanel } from '../../components/projects/CommentsPanel'
import type { ApplyAiSuggestionPayload } from '../../components/projects/CommentsPanel'
import { parseInstanceKey } from '../../components/projects/comments/commentAnchorText'
import { SubmitForReviewDialog } from '../../components/projects/SubmitForReviewDialog'
import { RightSidePanel } from '../../components/RightSidePanel'
import { ScrollPage } from '../../components/shell/ScrollPage'
import { listActiveComments } from '../../api/comments'
import type { AvailableTokenSection } from '../../components/ai/SectionDraftDialog'
import { CitationsLayer } from '../../components/editor/CitationsLayer'
import { DiscardDraftButton } from '../../components/projects/official-viewer/DiscardDraftButton'
import { convertAiPayloadForSection } from '../../components/projects/official-viewer/SectionEditor'
import { normalizeTableSchema } from '../../lib/structuredTableAdapter'
import { SectionTreeRenderer } from '../../components/projects/official-viewer/SectionTreeRenderer'
import {
  setValueAtPath,
  type TemplateDefinition,
  type TemplateI18nSection,
  type TemplateSectionMeta,
} from '../../components/projects/official-viewer/section_types'
import { pushToast } from '../../stores/toastStore'
import { useBreadcrumbExtension, useProjectContext } from './ProjectShell'
import { useRightPanel } from './rightPanelSlot'

/** Mensaje de cada motivo por el que no se puede editar el documento. */
const BLOCKED_REASON_KEY = {
  not_draft: 'official_editor.draft_only_warning',
  organization_read_only: 'official_editor.organization_read_only_warning',
  not_permitted: 'official_editor.edit_not_allowed_warning',
} as const

export function ProjectOfficialArtifactEditView() {
  const { t } = useTranslation(['projects', 'errors'])
  const apiErrorMessage = useApiErrorMessage()
  const { project } = useProjectContext()
  const { accessToken } = useAccessToken()
  const { me, isLoading: meIsLoading } = useMe()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { artifactId } = useParams({
    from: '/projects/$projectId/official/$artifactId/edit',
  })
  // Doc 07 — `?v=<id>` opcional: cuando se abre una versión draft
  // v(N+1) post-canon, el dialog navega aquí con ese id para que el
  // editor edite la draft nueva en lugar del current_version_id (que
  // sigue apuntando al canon vigente).
  const { v: searchVersionId } = useSearch({
    from: '/projects/$projectId/official/$artifactId/edit',
  })

  const artifactQuery = useQuery({
    queryKey: ['artifacts', artifactId],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return getArtifact(accessToken, artifactId)
    },
    enabled: !!accessToken,
    staleTime: 30_000,
    retry: false,
  })

  // OT-0009 §1.2 #1 (consecuencia) — el gate de "Editar" en el
  // Lifecycle evita el link, pero esta página es alcanzable por URL
  // directa. Mismo queryKey que `PhaseDrillIn`: si el usuario ya pasó
  // por Lifecycle, TanStack Query sirve el cache sin fetch nuevo.
  //
  // Deshabilitada para `external` — mismo motivo que en el viewer y el
  // preview: la query siempre falla para ese tier y bloqueaba la página
  // con un `ErrorBox` crudo en vez de la pantalla de "no tienes permiso"
  // que este gate de página construye más abajo; `canEditArtifact` ya
  // excluye a external antes de mirar `isMember`/`isLead`.
  // `!meIsLoading` evita la carrera con `useMe()` — ver nota en el viewer.
  const membersQuery = useQuery({
    queryKey: ['projects', project.id, 'members'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listProjectMembers(accessToken, project.id)
    },
    enabled: !!accessToken && !meIsLoading && !isExternal(me?.role),
    staleTime: 30_000,
    retry: false,
  })
  const { isMember, isLead, section } = useProjectAccess(project, membersQuery.data ?? [])

  // El snapshot es inmutable (congelado al crear el proyecto), así que solo
  // pedimos EL template de este artefacto — no el snapshot completo (~700KB).
  // `staleTime: Infinity` → un fetch por sesión y template.
  const templateKey = artifactQuery.data?.template_key ?? null
  const templateQuery = useQuery({
    queryKey: ['projects', project.id, 'snapshot', 'template', templateKey],
    queryFn: () => {
      if (!accessToken || !templateKey)
        throw new Error('no_access_token_or_template_key')
      return fetchSnapshotTemplate(accessToken, project.id, templateKey)
    },
    enabled: !!accessToken && !!templateKey,
    staleTime: Infinity,
    gcTime: 24 * 60 * 60_000,
    retry: false,
  })

  // Doc 07: si el caller pasó `?v=`, editamos esa versión (típicamente
  // una draft post-canon v(N+1)). Si no, caemos al current_version_id
  // del artifact (modo legacy: editar la versión actual, que puede ser
  // el draft inicial v1 o el canon vigente).
  const canonVersionId = artifactQuery.data?.current_version_id ?? null
  const editingVersionId = searchVersionId ?? canonVersionId
  const versionQuery = useQuery({
    queryKey: ['artifacts', artifactId, 'versions', editingVersionId],
    queryFn: () => {
      if (!accessToken || !editingVersionId)
        throw new Error('no_access_token_or_version')
      return getArtifactVersion(accessToken, artifactId, editingVersionId)
    },
    enabled: !!accessToken && !!editingVersionId,
    // OT-0064 — `is_editable` se decide con el estado vigente al abrir el
    // editor, no con la caché: volver con «Atrás» después de que otra
    // persona envió a revisión debe mostrar el bloqueo, no el editor. Con
    // `staleTime: 0` la versión se vuelve a pedir también cuando la key
    // cambia tras cargar el artifact (`refetchOnMount` sólo cubre el montaje).
    staleTime: 0,
    retry: false,
  })
  // El canon vigente — versión approved más reciente del artifact. Si
  // estamos editando una post-canon draft (v2+), necesitamos su
  // version_number para el banner y para el discard confirm copy.
  const canonVersionQuery = useQuery({
    queryKey: ['artifacts', artifactId, 'versions', canonVersionId],
    queryFn: () => {
      if (!accessToken || !canonVersionId)
        throw new Error('no_access_token_or_version')
      return getArtifactVersion(accessToken, artifactId, canonVersionId)
    },
    enabled:
      !!accessToken && !!canonVersionId && canonVersionId !== editingVersionId,
    staleTime: 60_000,
    retry: false,
  })

  // ADR-0001 Tier 1 — citas IA persistidas. CitationsLayer las consume
  // para mostrar el popover de fuente cuando el user hace click en `[N]`
  // dentro de cualquier sección rich_text del artifact.
  const citationsQuery = useVersionCitations({
    artifactId,
    versionId: editingVersionId,
  })

  /** Reporta una escritura fallida del editor (guardar, aplicar sugerencia
   * de IA, proponer estructura). Si el servidor la rechazó porque la versión
   * dejó de ser editable, la marca bloqueada: el editor pasa al aviso de
   * solo lectura en el mismo render. */
  function reportWriteError(err: unknown): void {
    if (isVersionLockError(err)) {
      markVersionLocked(queryClient, artifactId, editingVersionId)
    }
    pushToast({
      kind: 'critical',
      message:
        err instanceof ApiError
          ? apiErrorMessage(err)
          : t('official_editor.save_failed_toast'),
      duration: 6000,
    })
  }

  // Versión que este editor mostró editable. Si después se bloquea (un
  // guardado rechazado o un refetch que la trae en revisión), el aviso
  // de solo lectura advierte que lo no guardado no se conservó.
  const [editableSeenVersionId, setEditableSeenVersionId] = useState<
    string | null
  >(null)

  // ─── Local content state ─────────────────────────────────────────────────
  // `editedContent` es null hasta la primera edición (o guardado); mientras
  // tanto el editor muestra una copia del content de la version cargada.
  // Lo que se manda al PUT es `content`.
  const [editedContent, setContent] = useState<Record<string, unknown> | null>(
    null,
  )
  const loadedContent = useMemo(
    () =>
      versionQuery.data ? { ...(versionQuery.data.content ?? {}) } : null,
    [versionQuery.data],
  )
  const content = editedContent ?? loadedContent
  // Sprint AI-1.g — 5.2: estado del modal "Proponer estructura".
  const [proposeOpen, setProposeOpen] = useState(false)
  // Sprint AI-1.g — 5.4: estado del modal "Generar todo el documento".
  const [generateAllOpen, setGenerateAllOpen] = useState(false)
  // Doc 21 (Approval Workflow) — Fase 2.b: modal de "Enviar a revisión".
  const [submitOpen, setSubmitOpen] = useState(false)
  // Doc 21 Fase 5.d — RightSidePanel del editor (tabs: comments por
  // ahora, AI/citations en futuros sprints). El panel vive en el slot
  // del ProjectShell vía `useRightPanel` (ver `./rightPanelSlot`),
  // así no necesitamos `position: fixed` ni medir el área del editor.
  const [panelOpen, setPanelOpen] = useState(false)

  // Doc 21 Fase 5.d — comments del cycle anterior (regla v−1). El
  // count se usa en el tab badge del panel y para el submit gate
  // (Fase 5.f).
  // Doc 07 — Cuando editamos v(N+1) draft post-canon, pasamos la
  // version explícita: current_version_id todavía apunta a v(N) canon
  // y el gate debe contar comments de la draft (cycle=0 → vacío).
  const activeCommentsQuery = useQuery({
    queryKey: [
      'artifacts',
      artifactId,
      'active-comments',
      editingVersionId,
    ],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listActiveComments(accessToken, artifactId, {
        versionId: editingVersionId,
      })
    },
    enabled: !!accessToken,
    staleTime: 15_000,
    select: (rows) => rows.filter((r) => r.kind === 'comment').length,
  })
  const activeCommentsCount = activeCommentsQuery.data ?? 0

  // OT-0049 — observaciones de la verificación de IA que el autor aún no
  // marca como revisadas. Bloquean el envío igual que los comentarios; el
  // backend duplica la validación (`AI_OBSERVATIONS_PENDING`).
  const groundingsQuery = useVersionGroundings({
    artifactId,
    versionId: editingVersionId ?? null,
  })
  const pendingObservationsCount = pendingObservations(groundingsQuery.data ?? [])

  // Doc 21 Fase 6 — UX: si el autor entra al editor y hay comments por
  // subsanar del cycle anterior, abrimos el panel automáticamente. Sin
  // esto el autor tiene que descubrir el panel manualmente y los
  // comments quedan invisibles. Una sola vez por mount (no re-abre si
  // el usuario lo cierra y siguen los comments). Se ajusta en el render
  // en cuanto llega el conteo, sin un efecto que lo copie.
  const [didAutoOpenPanel, setDidAutoOpenPanel] = useState(false)
  if (!didAutoOpenPanel && activeCommentsCount > 0) {
    setDidAutoOpenPanel(true)
    setPanelOpen(true)
  }

  // ─── Snapshot template lookup ───────────────────────────────────────────
  const snapshotTemplate: SnapshotTemplateItem | null =
    templateQuery.data ?? null

  const templateDef: TemplateDefinition | null = useMemo(() => {
    if (!snapshotTemplate) return null
    return snapshotTemplate.definition as unknown as TemplateDefinition
  }, [snapshotTemplate])

  const renderLocale = useMemo<'es' | 'en'>(() => {
    if (!templateDef?.i18n) return 'es'
    const projectLang = project.content_language
    if (
      (projectLang === 'es' || projectLang === 'en') &&
      templateDef.i18n[projectLang]
    ) {
      return projectLang
    }
    if (templateDef.i18n.es) return 'es'
    if (templateDef.i18n.en) return 'en'
    return 'es'
  }, [templateDef, project.content_language])

  const i18nBlock = templateDef?.i18n?.[renderLocale]
  const i18nSectionsByCode = useMemo(() => {
    const m = new Map<string, TemplateI18nSection>()
    if (i18nBlock?.sections) {
      for (const s of i18nBlock.sections) m.set(s.section_id, s)
    }
    return m
  }, [i18nBlock])

  const orderedStructuralSections: TemplateSectionMeta[] = useMemo(() => {
    if (!templateDef?.sections) return []
    return [...templateDef.sections].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    )
  }, [templateDef])

  // Lista plana de TODAS las secciones del template (incluyendo nested)
  // disponibles para referenciar como `{{section_id.value}}` en el modal
  // de IA. Se computa una vez por render del editor — el modal filtra
  // out la sección activa.
  const availableTokenSections: AvailableTokenSection[] = useMemo(() => {
    if (!templateDef?.sections) return []
    return flattenTemplateForTokens(templateDef.sections, i18nSectionsByCode, [])
  }, [templateDef, i18nSectionsByCode])

  // Sprint AI-1.g — 5.2: ¿el template tiene repetibles? Si no, ocultamos
  // el botón "Proponer estructura" porque no haría nada.
  const hasRepeatables: boolean = useMemo(() => {
    if (!templateDef?.sections) return false
    const walk = (nodes: TemplateSectionMeta[]): boolean => {
      for (const node of nodes) {
        const children = node['children'] as
          | TemplateSectionMeta[]
          | undefined
        if (!Array.isArray(children) || children.length === 0) continue
        if (children.some((c) => c['repeatable'] === true)) return true
        if (walk(children)) return true
      }
      return false
    }
    return walk(templateDef.sections)
  }, [templateDef])

  // ─── Save mutation ───────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!accessToken || !content) throw new Error('no_context')
      // Doc 07 — pasamos `editingVersionId` para que el backend edite
      // la draft v(N+1) post-canon cuando aplique. Si es la v1 inicial,
      // `editingVersionId === current_version_id` y el resultado es
      // el mismo que pasar null (backwards-compat).
      return updateArtifactContent(
        accessToken,
        artifactId,
        content,
        editingVersionId,
      )
    },
    onSuccess: (newVersion) => {
      pushToast({ kind: 'success', message: t('official_editor.saved_toast') })
      // Refresh queries que dependen de version content
      void queryClient.invalidateQueries({
        queryKey: ['artifacts', artifactId],
      })
      void queryClient.invalidateQueries({
        queryKey: [
          'artifacts',
          artifactId,
          'versions',
          newVersion.id,
        ],
      })
      // El listing del Lifecycle drill-in también muestra updated_at +
      // version_number — invalidar todos los listings del proyecto para
      // que la próxima vez que el user vuelva al tab Lifecycle vea el
      // estado fresco. Match parcial vs el queryKey
      // ['projects', projectId, 'artifacts', 'by_phase', <phase>].
      void queryClient.invalidateQueries({
        queryKey: ['projects', project.id, 'artifacts'],
      })
      // El backend transiciona automáticamente la phase a `in_progress`
      // al primer save (sprint 3.6 de B.6.d.10) — refrescar el lifecycle
      // para que el círculo del timeline pase de gris a naranja.
      void queryClient.invalidateQueries({
        queryKey: ['projects', project.id, 'lifecycle'],
      })
      // El backend puede crear una nueva version (version_number+1) o
      // mutar la existente. Cualquiera de los dos: actualizamos el
      // state local con el content devuelto para que sea fuente de verdad.
      setContent({ ...(newVersion.content ?? {}) })
    },
    onError: reportWriteError,
  })

  // ─── Apply IA suggestion (Doc 21 Fase 7.g + Doc 24 rework) ──────────
  // Cuando el user clickea "Aplicar al borrador" en el modal de IA
  // co-reviewer, esta función:
  //   1. Resuelve el `section_type` del template.
  //   2. Si el backend devolvió `suggestedValue` (shape canónico
  //      tipado), lo aplica DIRECTO sin parser. Es el caso default
  //      post-rework — el agent emite JSON tipado por section_type.
  //   3. Solo si `suggestedValue` viene `null` (rows legacy pre-rework),
  //      cae al parser `convertAiPayloadForSection` sobre el markdown
  //      en `suggestedText`.
  //   4. Inyecta el value en `content` siguiendo el `instance_key` del
  //      comment (soporta repetibles via PathSegment).
  //   5. Persiste inmediatamente con `updateArtifactContent` y refresca
  //      el cache de queries dependientes.
  //
  // Diseño: NO usa `saveMutation` para evitar el problema de closure
  // stale — saveMutation lee `content` del closure al momento de
  // invocarse, pero acabamos de hacer `setContent` y el setState es
  // async. Llamar `updateArtifactContent` directo con el `updated`
  // recién computado garantiza que persiste lo correcto.
  const handleApplyAiSuggestion = async (
    payload: ApplyAiSuggestionPayload,
  ): Promise<void> => {
    if (!content || !accessToken) return
    const sectionMeta = _findSectionMeta(
      templateDef?.sections ?? [],
      payload.sectionId,
    )
    if (!sectionMeta) {
      pushToast({
        kind: 'critical',
        message: t('comments_ai_modal.toast.apply_unknown_section'),
        duration: 6000,
      })
      return
    }

    let newValue: unknown
    if (payload.suggestedValue !== null) {
      // Doc 24 rework — value canónico tipado por section_type. Aplica
      // directo sin parser. El agent ya devolvió la forma exacta que
      // el editor espera (lista, dict, string, etc.).
      newValue = payload.suggestedValue
    } else {
      // Compat legacy: rows pre-rework con solo markdown. Parser
      // type-aware sobre `suggestedText`.
      let cols: unknown[] | undefined
      if (sectionMeta.type === 'structured_table') {
        const normalized = normalizeTableSchema(
          sectionMeta as unknown as Parameters<typeof normalizeTableSchema>[0],
          project.content_language,
        )
        cols = normalized.columns
      } else {
        cols = (sectionMeta as unknown as { columns?: unknown[] }).columns
      }
      const defaultLang = (
        sectionMeta as unknown as { default_language?: string }
      ).default_language
      const opts: { columns?: unknown[]; defaultLanguage?: string } = {}
      if (cols !== undefined) opts.columns = cols
      if (defaultLang !== undefined) opts.defaultLanguage = defaultLang
      newValue = convertAiPayloadForSection(
        payload.suggestedText,
        sectionMeta.type,
        opts,
      )
    }

    const path = parseInstanceKey(payload.instanceKey || payload.sectionId)
    const updated = setValueAtPath(content, path, newValue)
    setContent(updated)

    try {
      const newVersion = await updateArtifactContent(
        accessToken,
        artifactId,
        updated,
        editingVersionId,
      )
      // El backend puede crear nueva version o mutar la existente;
      // sea cual sea, sincronizamos el state con la respuesta.
      setContent({ ...(newVersion.content ?? {}) })
      void queryClient.invalidateQueries({
        queryKey: ['artifacts', artifactId],
      })
      // El apply reemplaza en el servidor las citas y la verificación
      // de la sección (OT-0049): se refrescan para que el editor muestre
      // las observaciones vigentes y el bloqueo de envío cuente bien.
      if (editingVersionId) {
        void invalidateVersionAiMetadata(queryClient, editingVersionId)
      }
    } catch (err) {
      reportWriteError(err)
    }
  }

  // ─── Breadcrumb extension ────────────────────────────────────────────────
  // Añade "› Lifecycle › <artifact name>" al breadcrumb del shell.
  // Usa el name si está cargado, fallback al template_key.
  const artifactName =
    artifactQuery.data?.name ?? artifactQuery.data?.template_key ?? '…'
  useBreadcrumbExtension([
    { label: t('shell.tabs.lifecycle') },
    { label: artifactName },
  ])

  // ─── Render gates ────────────────────────────────────────────────────────
  if (
    meIsLoading ||
    artifactQuery.isLoading ||
    templateQuery.isLoading ||
    membersQuery.isLoading ||
    // Sin el veredicto pedido en esta visita no se pinta el editor con un
    // `is_editable` de caché.
    (editingVersionId && !versionQuery.isFetchedAfterMount) ||
    content === null
  ) {
    return (
      <ScrollPage padding="tight">
        <p className="font-mono text-xs text-ink-subtle">
          {t('official_viewer.loading')}
        </p>
      </ScrollPage>
    )
  }

  if (templateQuery.error) {
    return (
      <ScrollPage padding="tight">
        <ErrorBox error={templateQuery.error} />
      </ScrollPage>
    )
  }
  if (artifactQuery.error) {
    return (
      <ScrollPage padding="tight">
        <ErrorBox error={artifactQuery.error} />
      </ScrollPage>
    )
  }
  // `membersQuery` NO corta el render. Solo alimenta `isMember`/`isLead`,
  // que deciden si una acción se ofrece activa o deshabilitada — es una
  // pista de permisos, no el contenido de la página. Tumbar la lectura
  // del documento oficial porque falló listar miembros invierte la
  // mitigación de la propia OT-0009 (§8: "ante la duda, habilitar") y
  // convierte un fallo transitorio en una página inservible. Si falla,
  // `membersQuery.data ?? []` deja `isMember`/`isLead` en `false`: se
  // ofrece de menos, que es el lado seguro, y el backend sigue siendo
  // quien enforce.
  if (versionQuery.error) {
    return (
      <ScrollPage padding="tight">
        <ErrorBox error={versionQuery.error} />
      </ScrollPage>
    )
  }

  const artifact = artifactQuery.data
  if (!artifact) return null

  // OT-0064 — la regla de estado la decide el servidor (`is_editable`,
  // Doc 07): mira la VERSIÓN, no el status global del artifact, así que
  // una v(N+1) en borrador con canon vigente es editable y una versión
  // vigente en revisión no. El frontend no la recalcula.
  const versionIsEditable = versionQuery.data?.is_editable ?? false
  if (
    versionIsEditable &&
    editingVersionId &&
    editableSeenVersionId !== editingVersionId
  ) {
    setEditableSeenVersionId(editingVersionId)
  }

  // OT-0009 §1.2 #1 — el link de "Editar" del Lifecycle ya no ofrece
  // esta página a quien no puede editar (D11), pero la URL es
  // alcanzable directo. Sin este gate, el editor completo se monta
  // igual y el 403 llega recién al primer "Guardar" — exactamente el
  // caso más caro que la OT cierra.
  const canEdit = canEditArtifact(me?.role, me?.tenant?.access, {
    isMember,
    isLead,
    section,
    isOwner: artifact.owner_user_id === me?.id,
  })

  // Dos motivos de bloqueo distintos, cada uno con su propio mensaje —
  // uno es sobre el ESTADO del documento, el otro sobre PERMISOS. Mentir
  // sobre la causa (mostrar siempre "no es draft") sería tan honesto
  // como no explicar nada.
  const blockedReason: 'not_draft' | 'organization_read_only' | 'not_permitted' | null =
    !versionIsEditable
      ? 'not_draft'
      : !canWriteInTenant(me?.tenant?.access)
        ? 'organization_read_only'
        : !canEdit
          ? 'not_permitted'
          : null
  if (blockedReason) {
    return (
      <ScrollPage padding="tight">
        <div className="max-w-3xl">
          <Link
            to="/projects/$projectId/official/$artifactId"
            params={{ projectId: project.id, artifactId: artifact.id }}
            className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink mb-4"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {t('official_viewer.back_to_official')}
          </Link>
          <div className="rounded-md border border-review/40 bg-review-soft/40 p-4 font-body text-sm text-ink-muted">
            {t(BLOCKED_REASON_KEY[blockedReason])}
            {blockedReason === 'not_draft' &&
            editableSeenVersionId === editingVersionId ? (
              <p className="mt-2 font-medium text-ink">
                {t('official_editor.locked_while_editing_notice')}
              </p>
            ) : null}
          </div>
          <div className="mt-3">
            <Link
              to="/projects/$projectId/official/$artifactId"
              params={{ projectId: project.id, artifactId: artifact.id }}
              className="inline-flex items-center gap-1.5 text-sm text-brand hover:underline"
            >
              <Eye className="w-4 h-4" />
              {t('official_editor.view_button')}
            </Link>
          </div>
        </div>
      </ScrollPage>
    )
  }

  if (!snapshotTemplate || !templateDef) {
    return (
      <ScrollPage padding="tight">
        <div className="rounded-md border border-critical/40 bg-critical-soft p-4 font-body text-sm text-ink-muted">
          {t('official_viewer.errors.template_not_in_snapshot', {
            template_key: artifact.template_key,
          })}
        </div>
      </ScrollPage>
    )
  }

  const submitting = saveMutation.isPending

  // Doc 07 — ¿estamos editando una draft post-canon v(N+1)? Aquí la
  // versión ya pasó el gate de `is_editable`: basta con que haya una v
  // anterior (el canon).
  const editingVersion = versionQuery.data ?? null
  const isPostCanonDraft =
    !!editingVersion && editingVersion.version_number > 1
  const canonForBanner = canonVersionQuery.data ?? null

  return (
    // Layout del editor: flex column de altura completa.
    //  - Top:    toolbar de acciones del documento (shrink-0) — siempre
    //            visible, NO scrollea con el contenido. Mismo patrón que
    //            el bottom bar, solo que arriba.
    //  - Middle: contenido del editor con scroll interno (flex-1).
    //  - Bottom: action bar (shrink-0) — Cancelar / Guardar / Submit.
    //  Las tres filas viven en este flex, NO `position: fixed`,
    //  NO `sticky`, NO listeners. Flex se encarga del anclaje.
    <div className="h-full flex flex-col min-h-0">
      {/* ── Top toolbar — siempre visible (shrink-0) ────────────────
          Sale del `<header>` interno para que el usuario pueda invocar
          Generar / Preview / Comentarios sin importar dónde está
          scrolleado en el documento. El wrapper `max-w-5xl` alinea los
          botones al ras del borde derecho del documento (misma columna
          de contenido), no al borde de la pantalla. */}
      <div className="shrink-0 bg-surface border-b border-line px-8 py-2.5">
        <div className="max-w-5xl flex items-center justify-end gap-2">
        {/* Sprint AI-1.g — 5.4: Botón "Generar todo el documento".
            Orquesta StructureAgent (opcional) + SectionDraftAgent
            por cada sección editable. Auto-save entre secciones,
            continue-on-error. Antes de abrir, flushea cambios
            pendientes para que el orchestrator vea el state fresco. */}
        <button
          type="button"
          onClick={async () => {
            try {
              if (content) {
                await saveMutation.mutateAsync()
              }
            } catch {
              return
            }
            setGenerateAllOpen(true)
          }}
          disabled={submitting}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-white hover:bg-brand/90 transition-colors disabled:opacity-50"
        >
          <Sparkles className="w-3.5 h-3.5" />
          {t('official_editor.generate_all_button')}
        </button>
        {/* Sprint AI-1.g — 5.2: Botón "Proponer estructura". Solo
            aparece si el template tiene repetibles (sin ellos el
            StructureAgent no haría nada). El click abre el modal —
            el modal hace su propio estimate + propose. */}
        {hasRepeatables && (
          <button
            type="button"
            onClick={() => setProposeOpen(true)}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 rounded-md border border-brand/40 bg-brand/5 px-3 py-1.5 text-xs font-medium text-brand hover:bg-brand/10 transition-colors disabled:opacity-50"
          >
            <Layers className="w-3.5 h-3.5" />
            {t('official_editor.propose_structure_button')}
          </button>
        )}
        {/* Vista previa: flushea cambios pendientes (saveMutation
            sincrónica) antes de navegar — el preview hace su propio
            fetch desde BD, así garantizamos que ve la última edición.
            Si el save falla, no navegamos: el toast de error del
            saveMutation.onError ya lo comunica. */}
        <button
          type="button"
          onClick={async () => {
            try {
              await saveMutation.mutateAsync()
            } catch {
              return // toast onError ya disparó, no navegamos
            }
            navigate({
              to: '/projects/$projectId/official/$artifactId/preview',
              params: { projectId: project.id, artifactId: artifact.id },
            })
          }}
          disabled={submitting}
          className="inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-xs font-medium text-ink hover:border-line-strong hover:bg-surface-muted transition-colors disabled:opacity-50"
        >
          <FileText className="w-3.5 h-3.5" />
          {t('official_editor.preview_button')}
        </button>
        {/* Doc 21 Fase 5.d — toggle del RightSidePanel con tab de
            comments. Badge muestra count de comments del cycle
            anterior con status pending|addressed (regla v−1).
            Siempre el último a la derecha. */}
        <button
          type="button"
          onClick={() => setPanelOpen((v) => !v)}
          className={
            panelOpen
              ? 'inline-flex items-center gap-1.5 rounded-md border border-brand bg-brand-soft px-3 py-1.5 text-xs font-medium text-brand transition-colors'
              : 'inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-xs font-medium text-ink hover:border-line-strong hover:bg-surface-muted transition-colors'
          }
        >
          <MessageSquare className="w-3.5 h-3.5" />
          {t('comments_panel.tab_label')}
          {activeCommentsCount > 0 ? (
            <span className="px-1.5 py-0.5 rounded-full bg-review-soft text-review text-[10px] font-mono">
              {activeCommentsCount}
            </span>
          ) : null}
        </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-8 py-8">
        {/* `max-w-5xl` (sin `mx-auto`) replica la alineación a la
            izquierda que usa el viewer (ver `ArtifactPreview`).
            Mantiene consistencia visual entre editar y ver. */}
        <div className="max-w-5xl">
      <Link
        to="/projects/$projectId/lifecycle"
        params={{ projectId: project.id }}
        className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink mb-4"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {t('official_viewer.back_to_lifecycle')}
      </Link>

      {/* ADR-0002 Fase 10.f.2 + 10.e — banners de cross-refs:
          - Drift detectado (rojo)
          - Referencias consumidas en borrador (ámbar) */}
      {editingVersion ? (
        <EditorContextBanner
          captures={editingVersion.cross_artifact_captures ?? []}
          driftPending={editingVersion.cross_ref_drift_pending ?? false}
        />
      ) : null}

      {/* Doc 07 — Banner cuando editamos una draft v(N+1) post-canon:
          el oficial vigente sigue siendo v(N) y no se afecta. */}
      {isPostCanonDraft && editingVersion && canonForBanner ? (
        <div className="mb-6 rounded-md border border-review/30 bg-review-soft/40 px-4 py-3 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <p className="font-display text-sm font-semibold text-ink">
              {t('official_viewer.draft_banner.title', {
                n: editingVersion.version_number,
              })}
            </p>
            <p className="mt-0.5 font-body text-xs text-ink-muted">
              {t('official_viewer.draft_banner.body', {
                canon: canonForBanner.version_number,
              })}
            </p>
          </div>
          <DiscardDraftButton
            artifactId={artifact.id}
            projectId={project.id}
            versionId={editingVersion.id}
            versionNumber={editingVersion.version_number}
            canonVersionNumber={canonForBanner.version_number}
          />
        </div>
      ) : null}

      <header className="mb-6">
        <div className="min-w-0">
          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider bg-surface-muted text-ink-muted">
            {t('official_editor.draft_badge')}
          </span>
          <h1 className="font-display text-2xl font-semibold text-ink mt-1">
            {artifact.name}
          </h1>
          <p className="mt-1 font-mono text-xs text-ink-subtle">
            {artifact.template_key} · v{artifact.template_version}
          </p>
        </div>
      </header>

      {/* Sprint AI-1.g — 5.4: modal del orchestrator Generate-all. El
          orchestrator persiste cada sección al terminarla (auto-save
          en backend), así que aquí solo refrescamos las queries cuando
          termina exitosamente para que el editor muestre el contenido
          generado. */}
      <GenerateAllDialog
        open={generateAllOpen}
        onClose={() => setGenerateAllOpen(false)}
        artifactId={artifact.id}
        versionId={editingVersionId}
        i18nMap={i18nSectionsByCode}
        hasRepeatables={hasRepeatables}
        onCompleted={async () => {
          // Refresh con el contenido fresco del backend sin pasar por el
          // render gate de carga: si el view mostrara "cargando", se
          // desmontaría el GenerateAllDialog y el user nunca vería la
          // fase 'done'. Hacemos refetch manual y seteamos el content
          // directo.
          if (!accessToken || !editingVersionId) return
          await queryClient.invalidateQueries({
            queryKey: ['artifacts', artifactId],
          })
          // "Generar todo" guardó citas y verificación de cada sección.
          await invalidateVersionAiMetadata(queryClient, editingVersionId)
          const fresh = await getArtifactVersion(
            accessToken,
            artifactId,
            editingVersionId,
          )
          // Actualizar la cache de la query para que cualquier
          // observer (incluyendo el versionQuery del view) lo vea sin
          // hacer un refetch adicional.
          queryClient.setQueryData(
            ['artifacts', artifactId, 'versions', editingVersionId],
            fresh,
          )
          setContent({ ...(fresh.content ?? {}) })
          pushToast({
            kind: 'success',
            message: t('official_editor.generate_all_done_toast'),
          })
        }}
      />

      {/* Sprint AI-1.g — 5.2: modal del StructureAgent. Al aceptar, el
          modal entrega `final_proposal` (current + nuevos merged); aquí
          lo persistimos via PUT /content y refrescamos las queries para
          que el editor abajo muestre las instancias nuevas. */}
      {hasRepeatables && (
        <ProposeStructureDialog
          open={proposeOpen}
          onClose={() => setProposeOpen(false)}
          artifactId={artifact.id}
          versionId={editingVersionId}
          i18nMap={i18nSectionsByCode}
          onAccept={async (finalProposal) => {
            if (!accessToken) throw new Error('no_access_token')
            try {
              await updateArtifactContent(
                accessToken,
                artifactId,
                finalProposal,
                editingVersionId,
              )
            } catch (err) {
              // Bloqueo por estado: el editor pasa al aviso de solo lectura
              // (y el modal se desmonta con él). Cualquier otro error lo
              // muestra el propio modal.
              if (!isVersionLockError(err)) throw err
              reportWriteError(err)
              return
            }
            // Optimistic update local: el editor abajo ya ve las
            // instancias nuevas sin esperar al refetch.
            setContent({ ...finalProposal })
            // Y refrescamos cache de queries que dependen del
            // current_version.
            void queryClient.invalidateQueries({
              queryKey: ['artifacts', artifactId],
            })
            pushToast({
              kind: 'success',
              message: t('official_editor.propose_structure_saved'),
            })
          }}
        />
      )}

      <CitationsLayer citations={citationsQuery.data ?? []}>
      <div className="space-y-4">
        {orderedStructuralSections.map((meta) => (
          <SectionTreeRenderer
            key={meta.section_id}
            node={meta}
            parentPath={[]}
            content={content}
            onContentChange={(next) => setContent(next)}
            i18nMap={i18nSectionsByCode}
            disabled={submitting}
            ai={{
              artifactId: artifact.id,
              ...(editingVersionId ? { versionId: editingVersionId } : {}),
              projectLanguage: project.content_language,
              availableTokenSections,
              // Sprint AI-1.f.bug2: el modal de IA hace auto-save antes
              // de abrirse para que el backend lea la versión más fresca
              // del artifact (current_version.content). Sin esto, la IA
              // ve stale data y los context_sections / tokens resuelven
              // vacío. Si el save falla, el modal NO se abre y el toast
              // de error se muestra (saveMutation.onError).
              onBeforeAiOpen: async () => {
                if (!content) return
                await saveMutation.mutateAsync()
              },
            }}
          />
        ))}
      </div>
      </CitationsLayer>

      </div>{/* /max-w-5xl */}
      </div>{/* /scroll content */}

      {/* Bottom action bar — shrink-0 dentro del flex column del editor.
          Sin `position: fixed`, sin coordenadas absolutas: flexbox lo
          ancla al fondo del área del editor. Cuando el panel derecho
          abre, NO se solapa con el bar porque ambos viven en columnas
          diferentes del ProjectShell (panel a la derecha, editor —
          incluyendo este bar — a la izquierda). */}
      <div
        className="shrink-0 bg-surface border-t border-line px-8 py-3 flex items-center justify-end gap-2"
      >
        <button
          type="button"
          onClick={() =>
            navigate({
              to: '/projects/$projectId/official/$artifactId',
              params: { projectId: project.id, artifactId: artifact.id },
            })
          }
          disabled={submitting}
          className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
        >
          {t('official_editor.cancel')}
        </button>
        <button
          type="button"
          onClick={() => saveMutation.mutate()}
          disabled={submitting}
          className="px-5 py-2 border border-line hover:border-line-strong text-ink text-sm font-medium rounded-md disabled:opacity-50 hover:bg-surface-muted"
        >
          {submitting
            ? t('official_editor.saving')
            : t('official_editor.save')}
        </button>
        {/* Doc 21 — Fase 2.b + Fase 5.f: Submit for review con gate.
            Flushea pending saves antes de abrir el dialog. Si quedan
            comments del cycle anterior abiertos (status pending|
            addressed), el botón queda deshabilitado y el tooltip
            explica por qué. Backend duplica la validación con un 409
            COMMENTS_V_MINUS_1_OPEN si alguien bypassa. */}
        <button
          type="button"
          onClick={async () => {
            try {
              if (content) await saveMutation.mutateAsync()
            } catch {
              return // toast onError ya disparó
            }
            setSubmitOpen(true)
          }}
          disabled={
            submitting || activeCommentsCount > 0 || pendingObservationsCount > 0
          }
          title={
            activeCommentsCount > 0
              ? t('official_editor.submit_blocked_by_comments', {
                  count: activeCommentsCount,
                })
              : pendingObservationsCount > 0
                ? t('official_editor.submit_blocked_by_observations', {
                    count: pendingObservationsCount,
                  })
                : t('official_editor.submit_for_review_disabled_hint')
          }
          className="inline-flex items-center gap-1.5 px-5 py-2 bg-brand hover:bg-brand-deep text-canvas text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <GitBranch className="w-3.5 h-3.5" />
          {t('official_editor.submit_for_review_button')}
          {activeCommentsCount > 0 ? (
            <span className="px-1.5 py-0.5 rounded-full bg-canvas/20 text-[10px] font-mono">
              {activeCommentsCount}
            </span>
          ) : null}
        </button>
      </div>

      {/* Doc 21 — Fase 2.b: Submit for review dialog. */}
      <SubmitForReviewDialog
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        artifactId={artifact.id}
        projectId={project.id}
        subphaseCode={artifact.subphase_code}
        currentUserId={me?.id ?? null}
        authorUserId={artifact.created_by_user_id}
        onSubmit={async () => {
          if (!accessToken) throw new Error('no_access_token')
          // Doc 07 — pasamos editingVersionId para submitir la draft
          // v(N+1) post-canon correctamente. Para v1 inicial,
          // editingVersionId == current_version_id y el backend hace
          // lo mismo que cuando no se pasa.
          try {
            await submitForReview(
              accessToken,
              artifact.id,
              {},
              editingVersionId,
            )
          } catch (err) {
            // Bloqueo por estado (otra persona ya la envió, o dejó de ser
            // borrador): el editor pasa al aviso de solo lectura y el
            // diálogo se desmonta con él. Cualquier otro error lo muestra
            // el propio diálogo.
            if (!isVersionLockError(err)) throw err
            reportWriteError(err)
            return
          }
          pushToast({
            kind: 'success',
            message: t('submit_dialog.toast_success'),
          })
          // Tras submit, llevamos al user al viewer del artifact — ya no
          // es editable. Se sale ANTES de refrescar (OT-0064): con el
          // editor montado, la versión refrescada (`is_editable=false`)
          // pintaría un instante la vista de bloqueo con el aviso de
          // cambios perdidos, que aquí no es cierto.
          await navigate({
            to: '/projects/$projectId/official/$artifactId',
            params: { projectId: project.id, artifactId: artifact.id },
          })
          // Refresh artifact + project listings; el viewer verá
          // status=in_review (multi-stage) o status=approved + is_canon
          // (self-approve fast-path).
          await queryClient.invalidateQueries({
            queryKey: ['artifacts', artifact.id],
          })
          await queryClient.invalidateQueries({
            queryKey: ['projects', project.id, 'artifacts'],
          })
          await queryClient.invalidateQueries({
            queryKey: ['projects', project.id, 'lifecycle'],
          })
        }}
      />

      {/* Doc 21 Fase 5.d — RightSidePanel del editor: ahora se renderiza
          en el slot del ProjectShell (columna derecha del shell), no
          aquí inline. Aislamos `useRightPanel` en un sub-componente
          headless para respetar rules-of-hooks tras los early returns
          de carga/error de este editor. */}
      <EditorPanelSlot
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        artifactId={artifact.id}
        versionId={editingVersionId}
        artifactAuthorId={artifact.created_by_user_id}
        isLead={isLead}
        canUseAiSuggestions={canEdit}
        projectId={project.id}
        currentUserId={me?.id ?? null}
        i18nSectionsByCode={i18nSectionsByCode}
        activeCommentsCount={activeCommentsCount}
        versionContent={content}
        onApplyAiSuggestion={handleApplyAiSuggestion}
      />
    </div>
  )
}

// ─── Editor right-panel slot (headless) ──────────────────────────────
// Registra el RightSidePanel del editor en el slot del ProjectShell.
// Vive como sub-componente para que `useRightPanel` se llame siempre
// en el mismo orden cuando el padre ya pasó los gates de carga.
interface EditorPanelSlotProps {
  open: boolean
  onClose: () => void
  artifactId: string
  /** Doc 07 — version target (v(N+1) draft post-canon o current). */
  versionId: string | null
  artifactAuthorId: string | null
  /** OT-0009 — espejo de `_is_transition_allowed` para "Reponer"
   *  comentario: exige líder del proyecto, sin excepción de admin. */
  isLead: boolean
  /** OT-0016 — mismo `canEditArtifact` que ya calcula el padre para
   *  sus propios gates; gatea la asistencia de IA del panel (generar,
   *  aplicar, descartar: `load_artifact_for_edit` en el backend). */
  canUseAiSuggestions: boolean
  projectId: string
  currentUserId: string | null
  i18nSectionsByCode: Map<string, TemplateI18nSection>
  activeCommentsCount: number
  /** Doc 21 Fase 10 — content vivo que el autor está editando. El
   *  `CommentsPanel` lo usa para resolver el valor anclado a cada
   *  comment y mostrar el diff snapshot vs vivo en el card. */
  versionContent: Record<string, unknown> | null
  /** Doc 21 Fase 7.g — handler que aplica la sugerencia IA al editor. */
  onApplyAiSuggestion: (payload: ApplyAiSuggestionPayload) => void
}

function EditorPanelSlot({
  open,
  onClose,
  artifactId,
  versionId,
  artifactAuthorId,
  isLead,
  canUseAiSuggestions,
  projectId,
  currentUserId,
  i18nSectionsByCode,
  activeCommentsCount,
  versionContent,
  onApplyAiSuggestion,
}: EditorPanelSlotProps) {
  const { t } = useTranslation('projects')
  // `useRightPanel` devuelve un portal node (o null). Lo retornamos
  // como render output para que React lo monte directo en el host del
  // shell vía `createPortal`, sin estados intermedios.
  return useRightPanel(
    open ? (
      <RightSidePanel
        onClose={onClose}
        ariaLabel={t('comments_panel.tab_label')}
        tabs={[
          {
            key: 'comments',
            label: t('comments_panel.tab_label'),
            icon: <MessageSquare className="w-3.5 h-3.5" />,
            count: activeCommentsCount,
            content: (
              <CommentsPanel
                artifactId={artifactId}
                versionId={versionId}
                currentUserId={currentUserId}
                artifactAuthorId={artifactAuthorId}
                isLead={isLead}
                canUseAiSuggestions={canUseAiSuggestions}
                projectId={projectId}
                templateI18nMap={i18nSectionsByCode}
                versionContent={versionContent}
                onApplyAiSuggestion={onApplyAiSuggestion}
              />
            ),
          },
        ]}
      />
    ) : null,
  )
}

// ─── Section meta lookup (Doc 21 Fase 7.g) ──────────────────────────────────
/** Busca recursivamente una sección por `section_id` en el árbol del
 * template (incluye nested children). Usado por `handleApplyAiSuggestion`
 * para resolver el `section_type` + columns/default_language necesarios
 * para convertir el payload IA al shape del editor. */
function _findSectionMeta(
  sections: TemplateSectionMeta[],
  sectionId: string,
): TemplateSectionMeta | null {
  for (const s of sections) {
    if (s.section_id === sectionId) return s
    const children =
      (s['children'] as TemplateSectionMeta[] | undefined) ?? []
    if (children.length > 0) {
      const found = _findSectionMeta(children, sectionId)
      if (found) return found
    }
  }
  return null
}

// ─── Flatten template para token picker ────────────────────────────────────
/** Recorre el árbol del template (incluyendo `children` nested) y produce
 * una lista plana de secciones referenciables como `{{section_id.value}}`.
 * Marca como `is_repeatable` tanto los repetibles como los contenedores
 * que envuelven un repeatable child (porque devuelven un array al
 * resolver el token desde fuera).
 */
function flattenTemplateForTokens(
  sections: TemplateSectionMeta[],
  i18nMap: Map<string, TemplateI18nSection>,
  ancestorTitles: string[],
): AvailableTokenSection[] {
  const out: AvailableTokenSection[] = []
  for (const s of sections) {
    const i18n = i18nMap.get(s.section_id)
    const title = i18n?.title ?? s.section_id
    const children =
      (s['children'] as TemplateSectionMeta[] | undefined) ?? []
    const hasRepChild = children.some((c) => c['repeatable'] === true)
    const isRepeatable = s['repeatable'] === true
    out.push({
      section_id: s.section_id,
      title,
      path: ancestorTitles,
      is_repeatable: isRepeatable || hasRepChild,
    })
    if (children.length > 0) {
      out.push(
        ...flattenTemplateForTokens(children, i18nMap, [
          ...ancestorTitles,
          title,
        ]),
      )
    }
  }
  return out
}

// ─── ErrorBox ───────────────────────────────────────────────────────────────
function ErrorBox({ error }: { error: unknown }) {
  const { t } = useTranslation(['projects', 'errors'])
  const code = error instanceof ApiError ? error.code : 'UNKNOWN'
  const fallback =
    error instanceof ApiError
      ? error.body?.message ?? error.message
      : error instanceof Error
        ? error.message
        : 'unknown'
  return (
    <div className="rounded-md border border-critical/40 bg-critical-soft p-4 font-mono text-xs text-critical">
      {t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })}
    </div>
  )
}
